import { useEffect, useState } from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { GstinInput, fillFrom } from "@/components/gstinFetch";
import { api, errMsg } from "@/lib/api";
import { Field } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { OpeningFields, openingBody } from "@/components/opening";

const EMPTY_PARTY = { name: "", contact_person: "", phone: "", gstin: "", pan: "", address: "", city: "", state: "", opening_balance: "", opening_type: "receive", opening_date: "" };

export function PartyDialog({ open, onClose, prefillName, onSaved, initial }) {
  const [f, setF] = useState(EMPTY_PARTY);
  const [busy, setBusy] = useState(false);
  const { role } = useAuth();
  const canOpening = ["admin", "accounts"].includes(role);
  useEffect(() => { if (open) setF(initial ? { ...EMPTY_PARTY, ...initial } : { ...EMPTY_PARTY, name: prefillName || "" }); }, [open, prefillName, initial]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.name.trim()) return toast.error("Party name is required");
    setBusy(true);
    const body = { ...f, ...openingBody(f, "receive") };
    try {
      const { data } = initial
        ? await api.put(`/parties/${initial.id}`, body)
        : await api.post("/parties", body);
      toast.success(initial ? "Party updated" : `${data.name} saved to Party Master`);
      onSaved(data);
    } catch (e) {
      if (e?.response?.status === 400 && /already exists/i.test(errMsg(e))) {
        const { data } = await api.get("/parties", { params: { q: f.name } });
        const match = data.find((p) => p.name.toLowerCase() === f.name.trim().toLowerCase());
        if (match) return onSaved(match);
      }
      toast.error(errMsg(e));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto bg-card" data-testid="party-dialog">
        <DialogHeader><DialogTitle>{initial ? "Edit Party" : "Add New Party"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Party / Company Name *" className="col-span-2"><Input data-testid="party-name-input" value={f.name} onChange={set("name")} /></Field>
          <Field label="Contact Person"><Input data-testid="party-contact-input" value={f.contact_person} onChange={set("contact_person")} /></Field>
          <Field label="Phone"><Input data-testid="party-phone-input" value={f.phone} onChange={set("phone")} /></Field>
          <Field label="GST Number"><GstinInput testid="party-gstin-input" value={f.gstin} onChange={(g) => setF((x) => ({ ...x, gstin: g }))}
            onFetched={(d) => setF((x) => fillFrom(x, d, { name: "name", pan: "pan", address: "address", city: "city", state: "state" }))} /></Field>
          <Field label="PAN"><Input data-testid="party-pan-input" value={f.pan} onChange={set("pan")} /></Field>
          <Field label="Full Address" className="col-span-2"><Input data-testid="party-address-input" value={f.address} onChange={set("address")} /></Field>
          <Field label="City"><Input data-testid="party-city-input" value={f.city} onChange={set("city")} /></Field>
          <Field label="State"><Input data-testid="party-state-input" value={f.state} onChange={set("state")} /></Field>
          {canOpening && <OpeningFields f={f} setF={setF} kind="party" className="col-span-2" />}
        </div>
        <DialogFooter><Button data-testid="party-save-btn" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save Party"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PartyNameInput({ who, value, onChange, onPick, required = true }) {
  const [parties, setParties] = useState([]);
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState(false);
  useEffect(() => { api.get("/parties").then((r) => setParties(r.data)).catch(() => {}); }, []);
  const q = (value || "").trim().toLowerCase();
  const matches = q ? parties.filter((p) => `${p.name} ${p.gstin || ""} ${p.city || ""}`.toLowerCase().includes(q)).slice(0, 8) : [];
  const pick = (p) => { onPick(p); setOpen(false); setTyped(false); };
  const onBlur = () => setTimeout(() => {
    setOpen(false);
    const exact = typed && parties.find((p) => p.name.trim().toLowerCase() === q);
    if (exact) pick(exact);
  }, 150);
  return (
    <div className="relative">
      <Input data-testid={`${who}-name-input`} required={required} autoComplete="off" value={value} placeholder="Type party name — saved parties auto-fill"
        onChange={(e) => { onChange(e.target.value); setOpen(true); setTyped(true); }} onFocus={() => setOpen(true)} onBlur={onBlur} />
      {open && matches.length > 0 && (
        <div className="absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-md border bg-popover p-1 shadow-lg" data-testid={`${who}-suggestions`}>
          {matches.map((p) => (
            <button type="button" key={p.id} data-testid={`${who}-suggestion-${p.id}`} onMouseDown={(e) => { e.preventDefault(); pick(p); }}
              className="block w-full rounded-sm px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent">
              <div className="truncate font-medium">{p.name}</div>
              <div className="truncate text-xs text-muted-foreground">{[p.gstin, p.city, p.phone].filter(Boolean).join(" · ") || "Saved party"}</div>
            </button>
          ))}
        </div>
      )}
      {q && !parties.some((p) => p.name.trim().toLowerCase() === q) && <p className="mt-1 text-[11px] text-muted-foreground" data-testid={`${who}-new-party-hint`}>New party — just fill the details, it will be saved automatically with this LR.</p>}
    </div>
  );
}

export function PartyPicker({ who, value, onPick }) {
  const [open, setOpen] = useState(false);
  const [parties, setParties] = useState([]);
  const [query, setQuery] = useState("");
  const [dlg, setDlg] = useState(false);
  useEffect(() => { if (open) api.get("/parties").then((r) => setParties(r.data)).catch(() => {}); }, [open]);
  const filtered = parties.filter((p) => `${p.name} ${p.gstin} ${p.city}`.toLowerCase().includes(query.toLowerCase()));
  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" data-testid={`${who}-party-picker-btn`}
            className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-card px-3 text-sm shadow-sm transition-colors hover:border-ring">
            <span className={`truncate ${value ? "font-medium" : "text-muted-foreground"}`}>{value || "Search saved parties…"}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[--radix-popover-trigger-width] bg-popover p-0">
          <Command shouldFilter={false}>
            <CommandInput data-testid={`${who}-party-search-input`} placeholder="Search name, GSTIN, city…" value={query} onValueChange={setQuery} />
            <CommandList>
              <CommandEmpty className="text-muted-foreground">No saved party matches.</CommandEmpty>
              <CommandGroup heading="Saved Parties">
                {filtered.map((p) => (
                  <CommandItem key={p.id} value={p.id} data-testid={`${who}-party-option-${p.id}`}
                    onSelect={() => { onPick(p); setOpen(false); setQuery(""); }} className="cursor-pointer">
                    <Check className={value === p.name ? "opacity-100" : "opacity-0"} />
                    <div className="min-w-0">
                      <div className="truncate font-medium">{p.name}</div>
                      <div className="truncate text-xs text-muted-foreground">{[p.gstin, p.city, p.state].filter(Boolean).join(" · ") || "—"}</div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
            <div className="border-t p-1">
              <button type="button" data-testid={`${who}-add-new-party-btn`} onClick={() => { setDlg(true); setOpen(false); }}
                className="flex w-full items-center gap-2 rounded-sm px-2 py-2 text-sm font-semibold text-primary transition-colors hover:bg-accent">
                <Plus className="h-4 w-4" /> Add New Party{query ? ` "${query}"` : ""}
              </button>
            </div>
          </Command>
        </PopoverContent>
      </Popover>
      <PartyDialog open={dlg} onClose={() => setDlg(false)} prefillName={query} onSaved={(p) => { setDlg(false); setQuery(""); onPick(p); }} />
    </>
  );
}
